import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Save, X, AlertTriangle } from 'lucide-react';

// Import services
import AssignmentService from '../../services/assignmentService';
import { assetService } from '../../services/assetService';
import UserService from '../../services/userService';

// Import components
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const CreateAssetAssignment = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    assetId: '',
    assignedToId: '',
    department: '',
    assignedDate: new Date().toISOString().split('T')[0], // Today's date
    expectedReturnDate: '',
    location: '',
    purpose: '',
    notes: ''
  });

  // Validation state
  const [formErrors, setFormErrors] = useState({});
  const [formWarnings, setFormWarnings] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Load data on component mount
  useEffect(() => {
    loadAssets();
    loadEmployees();
  }, []);

  // Data loading functions
  const loadAssets = async () => {
    try {
      const response = await assetService.getAllAssets();
      setAvailableAssets(response.data || []);
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error('Failed to load assets');
    }
  };

  const loadEmployees = async () => {
    try {
      console.log('Loading employees for assignment form...');
      const response = await UserService.getUsers({
        limit: 100, // API maximum limit is 100
        page: 1
        // Removed status filter to get all users
      });

      console.log('Users API response:', response);

      // Handle different response structures
      let usersArray = [];

      if (response?.success && response?.data && Array.isArray(response.data)) {
        usersArray = response.data;
      } else if (response?.data && Array.isArray(response.data)) {
        usersArray = response.data;
      } else if (Array.isArray(response)) {
        usersArray = response;
      } else if (response?.users && Array.isArray(response.users)) {
        usersArray = response.users;
      }

      console.log('Extracted users array:', usersArray);

      if (usersArray.length > 0) {
        // Transform users to match the expected format
        const transformedEmployees = usersArray.map(user => ({
          id: user._id?.toString() || user.id?.toString(),
          _id: user._id?.toString() || user.id?.toString(),
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.username || 'Unknown User',
          email: user.email || '',
          phone: user.phone || user.phoneNumber || '',
          department: user.department || '',
          position: user.position || user.role || ''
        }));

        console.log('Transformed employees:', transformedEmployees);
        setEmployees(transformedEmployees);
      } else {
        console.warn('No users found in response. Response structure:', response);
        setEmployees([]);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        data: error.response?.data
      });
      setEmployees([]);
    }
  };

  // Form validation
  const validateField = (name, value) => {
    const errors = [];
    const warnings = [];

    switch (name) {
      case 'assetId':
        if (!value) {
          errors.push('Asset selection is required');
        }
        break;

      case 'assignedToId':
        if (!value) {
          errors.push('Employee selection is required');
        }
        break;

      case 'department':
        if (!value) {
          errors.push('Department selection is required');
        }
        break;

      case 'assignedDate':
        if (!value) {
          errors.push('Assignment date is required');
        } else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (selectedDate < today) {
            warnings.push('Assignment date is in the past');
          }
        }
        break;

      case 'expectedReturnDate':
        if (value) {
          const assignedDate = new Date(formData.assignedDate);
          const returnDate = new Date(value);

          if (returnDate <= assignedDate) {
            errors.push('Expected return date must be after assignment date');
          }
        }
        break;

      case 'location':
        if (!value) {
          errors.push('Location is required');
        }
        break;

      case 'purpose':
        if (!value) {
          errors.push('Purpose is required');
        }
        break;

      default:
        break;
    }

    return { errors, warnings };
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Validate field
    const { errors, warnings } = validateField(name, value);
    setFormErrors(prev => ({ ...prev, [name]: errors }));
    setFormWarnings(prev => ({ ...prev, [name]: warnings }));

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };

  const handleFormBlur = (e) => {
    const { name } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));

    // Re-validate on blur
    const { errors, warnings } = validateField(name, formData[name]);
    setFormErrors(prev => ({ ...prev, [name]: errors }));
    setFormWarnings(prev => ({ ...prev, [name]: warnings }));
  };

  const getValidationClass = (fieldName, formData, touchedFields) => {
    if (!touchedFields[fieldName]) return '';

    if (formErrors[fieldName] && formErrors[fieldName].length > 0) {
      return 'is-invalid';
    }

    return 'is-valid';
  };

  // Form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate all fields
      const allErrors = {};
      const allWarnings = {};

      Object.keys(formData).forEach(fieldName => {
        const { errors, warnings } = validateField(fieldName, formData[fieldName]);
        if (errors.length > 0) allErrors[fieldName] = errors;
        if (warnings.length > 0) allWarnings[fieldName] = warnings;
      });

      setFormErrors(allErrors);
      setFormWarnings(allWarnings);

      // Check if there are any errors
      if (Object.keys(allErrors).length > 0) {
        setIsLoading(false);
        return;
      }

      // Find selected asset and employee
      const selectedAsset = availableAssets.find(a =>
        a.assetCode === formData.assetId ||
        a.id === formData.assetId ||
        a._id === formData.assetId
      );

      const selectedEmployee = employees.find(e =>
        e._id === formData.assignedToId ||
        e.id === formData.assignedToId
      );

      if (!selectedAsset) {
        throw new Error('Selected asset not found');
      }

      if (!selectedEmployee) {
        throw new Error('Selected employee not found');
      }

      // Prepare assignment data
      const assignmentData = {
        assetId: selectedAsset._id || selectedAsset.id,
        assetCode: selectedAsset.assetCode || selectedAsset.assetId,
        assetName: selectedAsset.name,
        assetType: selectedAsset.type,
        assignedToId: selectedEmployee._id || selectedEmployee.id,
        assignedTo: selectedEmployee.name,
        assignedToEmail: selectedEmployee.email,
        assignedToPhone: selectedEmployee.phone,
        department: formData.department,
        assignedDate: formData.assignedDate,
        expectedReturnDate: formData.expectedReturnDate || null,
        location: formData.location,
        purpose: formData.purpose,
        notes: formData.notes,
        status: 'active',
        assignedBy: 'Current User' // TODO: Get from auth context
      };

      // Create assignment via API
      const response = await AssignmentService.createAssignment(assignmentData);

      toast.success('Assignment created successfully');

      // Navigate back to assignments list
      navigate('/assets/assignment');

    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation handlers
  const handleCancel = () => {
    navigate('/assets/assignment');
  };

  // Header actions
  const headerActions = [
    {
      label: 'Back to Assignments',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Return to assignments list'
    }
  ];

  return (
    <>
      {/* Breadcrumb */}
      <PageBreadcrumb />

      {/* Page Header */}
      <PageHeader
        title="Create Asset Assignment"
        description="Assign an asset to an employee with all necessary details"
        actions={headerActions}
      />

      <div className="page-container">
        {/* Assignment Form */}
        <div className="card">
          <div className="card-body">
            <form ref={formRef} onSubmit={handleFormSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Asset *
                  </label>
                  <select
                    name="assetId"
                    value={formData.assetId}
                    onChange={handleFormChange}
                    onBlur={handleFormBlur}
                    required
                    className={getValidationClass('assetId', formData, touchedFields)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: formErrors.assetId && touchedFields.assetId ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="">Select Asset</option>
                    {availableAssets && availableAssets.length > 0 ? (
                      availableAssets.map(asset => {
                        const assetValue = asset.assetCode || asset.assetId || asset.id || asset._id || '';
                        const assetKey = asset._id || asset.id || asset.assetCode || asset.assetId || Math.random();
                        return (
                          <option key={assetKey} value={assetValue}>
                            {asset.name || 'Unnamed Asset'} ({asset.type || 'Other'}) - {asset.condition || 'Good'}
                          </option>
                        );
                      })
                    ) : (
                      <option value="" disabled>No assets available</option>
                    )}
                  </select>
                  {formErrors.assetId && touchedFields.assetId && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {formErrors.assetId[0]}
                    </div>
                  )}
                  {formWarnings.assetId && touchedFields.assetId && (
                    <div style={{ color: '#f59e0b', fontSize: '12px', marginTop: '4px' }}>
                      {formWarnings.assetId[0]}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Assign to Employee *
                  </label>
                  <select
                    name="assignedToId"
                    value={formData.assignedToId}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(employee => (
                      <option key={employee.id || employee._id} value={employee._id || employee.id}>
                        {employee.name} - {employee.department || 'N/A'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Department *
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  >
                    <option value="">Select Department</option>
                    {(() => {
                      // Get unique departments from employees
                      const allDepartments = new Set();
                      employees.forEach(emp => {
                        if (emp.department) allDepartments.add(emp.department);
                      });
                      // Add common departments
                      ['IT', 'Operations', 'HR', 'Design', 'Sales', 'Marketing', 'Finance', 'Support', 'Legal', 'Procurement', 'Administration'].forEach(dept => allDepartments.add(dept));
                      // Sort and create options
                      return Array.from(allDepartments).sort().map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ));
                    })()}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Assignment Date *
                  </label>
                  <input
                    type="date"
                    name="assignedDate"
                    value={formData.assignedDate}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Expected Return Date
                  </label>
                  <input
                    type="date"
                    name="expectedReturnDate"
                    value={formData.expectedReturnDate}
                    onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleFormChange}
                    placeholder="e.g., Office Floor 2, Desk 15"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Purpose *
                  </label>
                  <input
                    type="text"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleFormChange}
                    placeholder="e.g., Development Work, Client Visits"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)'
                    }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    placeholder="Additional notes or comments..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  style={{
                    borderRadius: '0'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '0',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading ? (
                    <>
                      <Save size={16} className="animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Create Assignment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateAssetAssignment;
